import {
    InventoryItemContainerProps,
    UIInventoryItemContainerProps,
} from "./InventoryItemContainer";
import { useContext, useMemo } from "react";

import InteractiveInventoryItemIcon from "../InteractiveInventoryItemIcon/InteractiveInventoryItemIcon";
import InventoryItemDetailsPanel from "./InventoryItemDetailsPanel";
import InventoryItemTransferContext from "../../../../utils/sys/InventoryItemTransferContext";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import TextField from "../../../../common/components/display/TextField/TextField";
import Tooltip from "../../../../common/components/wrappers/Tooltip/Tooltip";

import {
    executeTransfers,
    validateTransfer,
} from "../../../../utils/sys/InventoryItemTransferUtils";
import pencilIcon from "../../../../assets/images/common/svg/pencil.svg";

import "./style/InventoryItemInfoSlot_style.css";

type Props = UIInventoryItemContainerProps & {
    className?: string;
    tooltipEnabled?: boolean;
    onEditRequested?: () => void;
};

export default function InventoryItemInfoSlot(props: Props): JSX.Element {
    const { transferTargetItemContainer, setTransferTargetItemContainer } =
        useContext(InventoryItemTransferContext);

    const emptyBGSrc =
        props.emptyBGSrc === undefined ? "back01" : props.emptyBGSrc;

    const tooltipContent = useMemo(() => {
        return <InventoryItemDetailsPanel item={props.item} />;
    }, [props.item?.id, props.item, props.tooltipEnabled]);

    const transferPayload: InventoryItemContainerProps = useMemo(
        () => ({
            id: props.id,
            collectionID: props.collectionID ?? undefined,
            item: props.item,
            itemRestrictor: props.itemRestrictor,
            transferVariables: props.transferVariables,
        }),
        [props.id, props.item, props.itemRestrictor]
    );

    return (
        <Tooltip
            tooltipContent={tooltipContent}
            enabled={props.tooltipEnabled && props.item != null}
            offsetX={20}
            offsetY={0}
            direction="auto-left-right"
        >
            <FlexRow
                className={`inventory-item-info-slot-container${
                    props.className != null ? ` ${props.className}` : ``
                }`}
                onClick={(event) => {
                    if (props.onClick == null) {
                        if (props.isEditable) {
                            if (transferTargetItemContainer == null) {
                                setTransferTargetItemContainer(transferPayload);
                            } else {
                                if (
                                    transferTargetItemContainer.id === props.id
                                ) {
                                    setTransferTargetItemContainer(null);
                                } else if (
                                    validateTransfer(
                                        transferPayload,
                                        transferTargetItemContainer
                                    )
                                ) {
                                    executeTransfers({
                                        transfers: [
                                            {
                                                item: transferTargetItemContainer.item,
                                                setItemCallback:
                                                    transferPayload
                                                        .transferVariables
                                                        ?.setItemCallback,
                                            },
                                            {
                                                item: transferPayload.item,
                                                setItemCallback:
                                                    transferTargetItemContainer
                                                        .transferVariables
                                                        ?.setItemCallback,
                                            },
                                        ],
                                        onComplete: () => {
                                            props.transferVariables?.onTransferSequenceComplete?.();

                                            if (
                                                transferTargetItemContainer
                                                    .transferVariables
                                                    ?.onTransferSequenceComplete !=
                                                    null &&
                                                transferTargetItemContainer
                                                    .transferVariables
                                                    ?.onTransferSequenceComplete !==
                                                    props.transferVariables
                                                        ?.onTransferSequenceComplete
                                            ) {
                                                transferTargetItemContainer.transferVariables.onTransferSequenceComplete();
                                            }
                                        },
                                    });

                                    setTransferTargetItemContainer(null);
                                }
                            }
                        }
                    } else {
                        props.onClick({
                            event,
                            target: transferPayload,
                        });
                    }

                    event?.stopPropagation();
                }}
            >
                <div className="icon-container">
                    <InteractiveInventoryItemIcon
                        className="icon"
                        bgSrc={
                            props.item == null ? emptyBGSrc : props.item.bgSrc
                        }
                        bgColor={props.item == null ? null : props.item.bgColor}
                        iconSrc={
                            props.item == null
                                ? props.emptyIconSrc
                                : props.item.iconSrc
                        }
                        iconColor={
                            props.item == null ? null : props.item.iconColor
                        }
                        highlightColor={
                            props.isSelected
                                ? "active"
                                : props.isValidTarget
                                ? "valid"
                                : undefined
                        }
                    />
                </div>
                <div
                    className={`details-container${
                        props.isSelected ? " active" : ""
                    }${
                        props.isValidTarget && !props.isSelected ? " valid" : ""
                    }`}
                >
                    <FlexColumn>
                        <FlexRow width="full">
                            <TextField className={"title-bar"}>
                                {(props.item?.title.length ?? 0) > 0
                                    ? props.item?.title ?? "<Unnamed Item>"
                                    : "<Unnamed Item>"}
                            </TextField>
                            {props.isEditable === true && (
                                <FlexRow gap={8} width="auto">
                                    <button
                                        onClick={(e) => {
                                            props.onEditRequested?.();
                                            e.stopPropagation();
                                        }}
                                    >
                                        <img
                                            src={pencilIcon}
                                            alt="..."
                                            style={{ width: "12px" }}
                                        />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            props.transferVariables?.setItemCallback?.(
                                                null
                                            );
                                            props.transferVariables?.onTransferSequenceComplete?.();
                                            e.stopPropagation();
                                        }}
                                    >
                                        X
                                    </button>
                                </FlexRow>
                            )}
                        </FlexRow>
                        <TextField>{`${props.item?.typeString}${
                            props.item?.subTypeString == null
                                ? ``
                                : ` - ${props.item.subTypeString}`
                        }`}</TextField>
                        <FlexRow>
                            <TextField>
                                {props.item?.description ?? ""}
                            </TextField>
                        </FlexRow>
                    </FlexColumn>
                </div>
            </FlexRow>
        </Tooltip>
    );
}
